import React, { useEffect, useState, useContext } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Paper,
  Divider,
  Stack,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
} from '@mui/material';
import { ContentCopy, Download, Refresh } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { ChatProvider, ChatContext } from '@/context/ChatContext';
import ChatContent from '@/components/chats/ChatContent';
import ChatMsgSent from '@/components/chats/ChatMsgSent';
import AppCard from '@/components/shared/AppCard';
import { apiService } from '@/services/api';
import { useAuth } from '@/context/AuthContext.tsx';
import { useTranslation } from '@/hooks/useTranslation';
import { ENCODE_TYPE_VALUES, EncodeType } from '@/types/apps/fileService';

interface FileServiceDetails {
  id: string;
  shortId?: string | null;
  status: 'PROCESSING' | 'COMPLETED' | 'CLOSED' | 'CANCELLED';
  vehicleType: string;
  carBrand: string;
  carModel: string;
  carEngine: string;
  carPower: string;
  carYear: string;
  carGearbox: string;
  carFuelType: string;
  ecu: string;
  vinCode?: string | null;
  extendedFileName?: string | null;
  readingTool: string;
  customReadingTool?: string | null;
  readingMode?: string | null;
  hasNewMessage?: boolean | null;
  systemDescription?: string | null;
  creditsAmount?: number | null;
  originalFileUrl?: string | null;
  modifiedFileUrl?: string | null;
  decodedOriginalFileUrl?: string | null;
  memoryType?: string | null;
  isDecrypted?: boolean | null;
  createdAt: string;
  updatedAt: string;
  solutions: string[];
  autotunerEmail?: string | null;
  magicmotorsportsEmail?: string | null;
  customer?: {
    companyName?: string | null;
    firstName: string;
    lastName: string;
    email: string;
    creditsAmount?: number | null;
    customerTypeId?: string | null;
    customerTypeName?: string | null;
    slaveSerialNumber?: string | null;
  } | null;
}

const FileServiceChat: React.FC<{
  fileId: string;
  title: string;
  status: string;
  customer?: {
    companyName?: string | null;
    firstName: string;
    lastName: string;
    email: string;
    creditsAmount?: number | null;
    customerTypeId?: string | null;
    customerTypeName?: string | null;
    slaveSerialNumber?: string | null;
  } | null;
  refreshKey?: number;
}> = ({ fileId, title, status, customer, refreshKey }) => {
  const { loadFileMessages } = useContext(ChatContext);

  useEffect(() => {
    loadFileMessages(fileId, { name: title, status, customer });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileId, title, status, customer, refreshKey]);

  return (
    <AppCard>
      <Box flexGrow={1}>
        <ChatContent />
        <ChatMsgSent fileId={fileId} />
      </Box>
    </AppCard>
  );
};

const CreatedFileServicePage: React.FC = () => {
  const { fileId } = useParams<{ fileId: string }>();
  const { t } = useTranslation();
  const [fileDetails, setFileDetails] = useState<FileServiceDetails | null>(
    null,
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [statusSelect, setStatusSelect] = useState<
    'PROCESSING' | 'COMPLETED' | 'CLOSED' | 'CANCELLED'
  >('PROCESSING');
  const [statusFile, setStatusFile] = useState<File | null>(null);
  const [encodeTypeSelect, setEncodeTypeSelect] = useState<EncodeType | ''>('');
  const { user } = useAuth();
  const [chatRefreshKey, setChatRefreshKey] = useState(0);

  useEffect(() => {
    const fetchFileDetails = async () => {
      if (!fileId) {
        setError(t('createdFileService.errors.fileIdMissing'));
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await apiService.getFile(fileId);
        setFileDetails(response);
      } catch (err: any) {
        console.error('Error loading file service:', err);
        setError(err.message || t('createdFileService.errors.failedToLoad'));
      } finally {
        setLoading(false);
      }
    };

    fetchFileDetails();
  }, [fileId]);

  const handleStatusFileChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.files && event.target.files[0]) {
      setStatusFile(event.target.files[0]);
    } else {
      setStatusFile(null);
    }
  };

  const handleCopyEmail = (email: string) => {
    navigator.clipboard.writeText(email).catch(() => {});
  };

  const handleStatusChange = async () => {
    if (!fileDetails || !fileId) return;
    if (statusSelect === 'COMPLETED' && !statusFile) {
      setStatusError(t('createdFileService.errors.uploadRequired'));
      return;
    }

    const isFlexSlave = fileDetails.readingTool === 'FLEX_SLAVE';

    if (statusSelect === 'COMPLETED' && isFlexSlave && !encodeTypeSelect) {
      setStatusError(t('createdFileService.errors.encodeTypeRequired'));
      return;
    }

    try {
      setStatusUpdating(true);
      setStatusError(null);

      const response = await apiService.updateFileStatus(fileId, {
        status: statusSelect,
        file: statusFile || undefined,
        encodeType:
          statusSelect === 'COMPLETED' && isFlexSlave
            ? encodeTypeSelect || undefined
            : undefined,
      });

      setFileDetails(prev =>
        prev
          ? {
              ...prev,
              status: response.status,
              updatedAt: response.updatedAt,
            }
          : prev
      );
      setStatusModalOpen(false);
      setStatusFile(null);
      setEncodeTypeSelect('');
    } catch (err: any) {
      setStatusError(
        err.message || t('createdFileService.errors.failedToUpdate'),
      );
    } finally {
      setStatusUpdating(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !fileDetails || !fileId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert variant="filled" severity="error">
          {error || t('createdFileService.errors.notFound')}
        </Alert>
      </Box>
    );
  }

  const fileTitle = `${fileDetails.extendedFileName}`;

  const readingTools = [
    {
      id: 'MASTERTOOL',
      name: t('newFileService.readingTool.mastertool.name'),
      description: t('newFileService.readingTool.mastertool.description'),
    },
    {
      id: 'AUTOTUNER_SLAVE',
      name: t('newFileService.readingTool.autotunerSlave.name'),
      description: t('newFileService.readingTool.autotunerSlave.description'),
    },
    {
      id: 'FLEX_SLAVE',
      name: t('newFileService.readingTool.flexSlave.name'),
      description: t('newFileService.readingTool.flexSlave.description'),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Card sx={{ mb: 3 }}>
        <CardContent
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h5" fontWeight={600}>
              {t('createdFileService.title', { id: fileDetails.id })}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('createdFileService.created', {
                date: new Date(fileDetails.createdAt).toLocaleString(),
              })}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
              justifyContent: 'flex-end',
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              {/* Refresh chat messages */}
              <Tooltip title={t('createdFileService.refreshMessages')}>
                <span>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => setChatRefreshKey(k => k + 1)}
                  >
                    <Refresh fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>

              {/* File download buttons - visible for admin and customer */}
              <Stack direction="row" spacing={1} alignItems="center">
              <Tooltip title={t('createdFileService.sections.files.downloadOriginal')}>
                <span>
                  <IconButton
                    size="small"
                    color="primary"
                    disabled={!fileDetails.originalFileUrl}
                    component="a"
                    href={fileDetails.originalFileUrl || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Download fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>

              {fileDetails.decodedOriginalFileUrl && (
                <Tooltip
                  title={t(
                    'createdFileService.sections.files.downloadDecodedOriginal',
                  )}
                >
                  <span>
                    <IconButton
                      size="small"
                      color="primary"
                      component="a"
                      href={fileDetails.decodedOriginalFileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Download fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}

              <Tooltip title={t('createdFileService.sections.files.downloadModified')}>
                <span>
                  <IconButton
                    size="small"
                    color="secondary"
                    disabled={!fileDetails.modifiedFileUrl}
                    component="a"
                    href={fileDetails.modifiedFileUrl || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Download fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              </Stack>

              {user?.userType === 'admin' && (
              <Button
                variant="outlined"
                onClick={() => {
                  setStatusSelect(fileDetails.status);
                  setStatusModalOpen(true);
                }}
                disabled={fileDetails.status === 'CANCELLED'}
              >
                {t('createdFileService.changeStatus')}
              </Button>
              )}
            </Stack>
          </Box>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 8 }}>
          <ChatProvider>
            <FileServiceChat
              fileId={fileId}
              title={fileTitle}
              status={fileDetails.status.toLowerCase()}
              customer={fileDetails.customer}
              refreshKey={chatRefreshKey}
            />
          </ChatProvider>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Stack spacing={3}>
            <Paper
              sx={{
                p: 3,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                {t('createdFileService.sections.vehicle.title')}
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {fileDetails.carBrand} · {fileDetails.carModel}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('createdFileService.sections.vehicle.type', {
                  type: fileDetails.vehicleType,
                })}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" color="text.secondary">
                {t('createdFileService.sections.vehicle.engine', {
                  engine: fileDetails.carEngine,
                })}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('createdFileService.sections.vehicle.power', {
                  power: fileDetails.carPower,
                })}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('createdFileService.sections.vehicle.year', {
                  year: fileDetails.carYear,
                })}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('createdFileService.sections.vehicle.gearbox', {
                  gearbox: fileDetails.carGearbox,
                })}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('createdFileService.sections.vehicle.fuel', {
                  fuel: fileDetails.carFuelType,
                })}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('createdFileService.sections.vehicle.ecu', {
                  ecu: fileDetails.ecu,
                })}
              </Typography>
              {fileDetails.vinCode && (
                <Typography variant="body2" color="text.secondary">
                  {t('createdFileService.sections.vehicle.vin', {
                    vin: fileDetails.vinCode,
                  })}
                </Typography>
              )}
            </Paper>

            <Paper
              sx={{
                p: 3,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                {t('createdFileService.sections.details.title')}
              </Typography>
              <Stack spacing={1}>
                <Typography variant="body2" color="text.secondary">
                  {t('createdFileService.sections.details.readingTool', {
                    tool:
                      readingTools.find(
                        tool => tool.id === fileDetails.readingTool,
                      )?.name || '',
                  })}
                </Typography>
                {fileDetails.readingMode && (
                  <Typography variant="body2" color="text.secondary">
                    {t('createdFileService.sections.details.readingMode', {
                      mode: fileDetails.readingMode,
                    })}
                  </Typography>
                )}
                {fileDetails?.customer?.slaveSerialNumber && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        flexWrap: 'wrap',
                      }}
                    >
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.secondary"
                      >
                        Slave serial number:
                      </Typography>
                      <Chip
                        label={fileDetails?.customer?.slaveSerialNumber}
                        variant="outlined"
                        size="small"
                      />
                      <Tooltip title={t('createdFileService.copyToClipboard')}>
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleCopyEmail(fileDetails?.customer?.slaveSerialNumber || '')
                          }
                          aria-label={t('createdFileService.copyToClipboard')}
                        >
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                <Typography variant="body2" color="text.secondary">
                  {t('createdFileService.sections.details.credits', {
                    credits: fileDetails.creditsAmount ?? '—',
                  })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('createdFileService.sections.details.updated', {
                    date: new Date(fileDetails.updatedAt).toLocaleString(),
                  })}
                </Typography>
              </Stack>
            </Paper>

            {user?.userType === 'admin' && fileDetails.systemDescription && (
                <Paper
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    {t('createdFileService.sections.systemDescription.title')}
                  </Typography>
                  <Stack spacing={1}>
                    {fileDetails.systemDescription && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ whiteSpace: 'pre-wrap' }}
                      >
                        {fileDetails.systemDescription}
                      </Typography>
                    )}
                  </Stack>
                </Paper>
              )}

            <Paper
              sx={{
                p: 3,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                {t('createdFileService.sections.solutions.title')}
              </Typography>
              {fileDetails.solutions.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t('createdFileService.sections.solutions.noSolutions')}
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {fileDetails.solutions.map(solution => (
                    <Chip
                      key={solution}
                      label={solution}
                      variant="outlined"
                      size="small"
                    />
                  ))}
                </Stack>
              )}
            </Paper>

          </Stack>
        </Grid>
      </Grid>
      <Dialog
        open={statusModalOpen}
        onClose={() => {
          setStatusModalOpen(false);
          setStatusError(null);
          setStatusFile(null);
          setEncodeTypeSelect('');
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t('createdFileService.statusModal.title')}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3}>
            {statusError && (
              <Alert variant="filled" severity="error" onClose={() => setStatusError(null)}>
                {statusError}
              </Alert>
            )}
            <FormControl fullWidth>
              <InputLabel>
                {t('createdFileService.statusModal.statusLabel')}
              </InputLabel>
              <Select
                label={t('createdFileService.statusModal.statusLabel')}
                value={statusSelect}
                onChange={event =>
                  setStatusSelect(
                    event.target.value as
                      | 'PROCESSING'
                      | 'COMPLETED'
                      | 'CLOSED'
                      | 'CANCELLED',
                  )
                }
              >
                <MenuItem value="PROCESSING">
                  {t('fileService.status.processing')}
                </MenuItem>
                <MenuItem value="COMPLETED">
                  {t('fileService.status.completed')}
                </MenuItem>
                <MenuItem value="CLOSED">
                  {t('fileService.status.closed')}
                </MenuItem>
                {fileDetails.status !== 'COMPLETED' && (
                  <MenuItem value="CANCELLED">
                    {t('fileService.status.cancelled')}
                  </MenuItem>
                )}
              </Select>
            </FormControl>

            {statusSelect === 'COMPLETED' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {fileDetails.readingTool === 'FLEX_SLAVE' && (
                  <FormControl fullWidth required>
                    <InputLabel>
                      {t('createdFileService.statusModal.encodeTypeLabel')}
                    </InputLabel>
                    <Select
                      label={t('createdFileService.statusModal.encodeTypeLabel')}
                      value={encodeTypeSelect}
                      onChange={event =>
                        setEncodeTypeSelect(event.target.value as EncodeType | '')
                      }
                    >
                      {ENCODE_TYPE_VALUES.map(value => (
                        <MenuItem key={value} value={value}>
                          {t(`createdFileService.statusModal.encodeType.${value}`)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {user?.userType === 'admin' &&
                  fileDetails.readingTool === 'FLEX_SLAVE' &&
                  !fileDetails.customer?.slaveSerialNumber && (
                    <Alert severity="warning">
                      {t('createdFileService.statusModal.noSlaveSerialNumber')}
                    </Alert>
                  )}
                <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {t('createdFileService.statusModal.uploadModifiedFile')}
                  </Typography>
                  <Button
                    variant="outlined"
                    component="label"
                    sx={{ textTransform: 'none' }}
                  >
                    {statusFile
                      ? statusFile.name
                      : t('createdFileService.statusModal.selectFile')}
                    <input type="file" hidden onChange={handleStatusFileChange} />
                  </Button>
                </Box>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setStatusModalOpen(false);
              setStatusError(null);
              setStatusFile(null);
              setEncodeTypeSelect('');
            }}
            disabled={statusUpdating}
          >
            {t('createdFileService.statusModal.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleStatusChange}
            disabled={statusUpdating}
          >
            {statusUpdating ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              t('createdFileService.statusModal.update')
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CreatedFileServicePage;
